/* @flow */

import type { KeyValuePair } from '../../../../helpers/jsHelpers/types';
import type { ETAG_MAP } from './ETag';
import type { NETGEM_API_V8_ITEM_LOCATION } from './FeedItem';
import type { NETGEM_API_V8_FEED_RESULT } from './FeedResult';

export const ScheduledRecordingsKind = Object.freeze({
  KeepFromReplay: 'KeepFromReplay',
  Series: 'Series',
  Single: 'Single',
});

// Cannot convert to Flow enum because it's used to refine types
export type ScheduledRecordingsKindType = typeof ScheduledRecordingsKind.KeepFromReplay | typeof ScheduledRecordingsKind.Series | typeof ScheduledRecordingsKind.Single;

export type NETGEM_API_V8_RECORDINGS_STATUS = {
  count: number,
  duration: string,
};

export type NETGEM_API_V8_RECORDINGS_FUTURE = {
  ...NETGEM_API_V8_FEED_RESULT,
  current: NETGEM_API_V8_RECORDINGS_STATUS,
  expectedError: NETGEM_API_V8_RECORDINGS_STATUS,
  expectedSuccess: NETGEM_API_V8_RECORDINGS_STATUS,
  maxQuota: NETGEM_API_V8_RECORDINGS_STATUS,
};

export enum RecordingOutcome {
  ExceededConcurrency = 'ExceededConcurrency',
  HistoryOverflow = 'HistoryOverflow',
  OutdatedEpisode = 'OutdatedEpisode',
  OutOfQuota = 'OutOfQuota',
  Rebroadcast = 'Rebroadcast',
  Recorded = 'Recorded',
  ServerError = 'ServerError',
  Unknown = 'Unknown',
  WillBeRecorded = 'WillBeRecorded',
}

export type NETGEM_API_V8_MATCHING_SCHEDULED_RECORDING = {
  conflict: Array<string>,
  id: string,
  quotaUsed: string,
  recordOutcome: RecordingOutcome,
};

// Keys are program Ids
export type NETGEM_RECORDINGS_MAP = KeyValuePair<Array<NETGEM_API_V8_ITEM_LOCATION>>;

export type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_SINGLE = {
  kind: typeof ScheduledRecordingsKind.Single,
  requestedScheduledEventId: string,
  targetScheduledEventId: string,
};

export type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_SERIES = {
  fromUtc: string,
  increasingEpisodes?: boolean,
  kind: typeof ScheduledRecordingsKind.Series,
  recordsToKeep: ?number,
  seriesId: string,
  toUtc?: string,
};

export type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_KEEP_FROM_REPLAY = {
  catchupId: string,
  kind: typeof ScheduledRecordingsKind.KeepFromReplay,
};

type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_ANY =
  NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_SINGLE | NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_SERIES | NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_KEEP_FROM_REPLAY;

type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_COMMON = {
  startMargin: string,
  endMargin: string,
  channelId: string,
};

export type NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION = {
  ...NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_COMMON,
  ...NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION_ANY,
};

export type ID_TYPE = {
  id: string,
};

export type NETGEM_API_V8_SCHEDULED_RECORDING = {
  description: NETGEM_API_V8_SCHEDULED_RECORDING_DESCRIPTION,
  id: string,
  records: Array<ID_TYPE>,
};

// Keys are series or scheduled event Ids
export type NETGEM_SCHEDULED_RECORDINGS_MAP = KeyValuePair<NETGEM_API_V8_SCHEDULED_RECORDING>;

export type NETGEM_SCHEDULED_RECORDINGS_MAP_AND_ETAGS = {
  eTags: ETAG_MAP,
  scheduledRecordings: NETGEM_SCHEDULED_RECORDINGS_MAP,
};

export type NETGEM_API_V8_SCHEDULED_RECORDINGS_LIST_RESULT = Array<ID_TYPE>;

export type NETGEM_API_V8_SCHEDULED_RECORDINGS_LIST_FULL_RESULT = Array<NETGEM_API_V8_SCHEDULED_RECORDING>;

// Duration in seconds
export type NETGEM_RECORDINGS_STATUS_ITEM = {
  count: number,
  duration: number,
};

export type NETGEM_FUTURE_RECORDINGS_STATUS = {
  current: NETGEM_RECORDINGS_STATUS_ITEM,
  expectedError: NETGEM_RECORDINGS_STATUS_ITEM,
  expectedSuccess: NETGEM_RECORDINGS_STATUS_ITEM,
  maxQuota: NETGEM_RECORDINGS_STATUS_ITEM,
};

export const DEFAULT_RECORDINGS_STATUS_ITEM: NETGEM_RECORDINGS_STATUS_ITEM = { count: 0, duration: 0 };
